import React from "react";

/* Single source of truth for the backend URL and all API calls.
   Everything else should import from window.api — never hard-code the URL
   or fetch logic elsewhere. */

(function () {
  const BASE_URL =
    (typeof window !== "undefined" && window.__API_BASE__) ||
    "http://localhost:5001";

  // ── Local storage keys ────────────────────────────────────────────────
  const PENDING_PROFILE_KEY = "cn_pending_profile";
  const PENDING_PLAN_KEY = "cn_pending_search_plan";
  const PROFILE_ID_KEY = "cn_profile_id";
  const AUTH_KEY = "cn_auth_v1";
  const USER_ID_KEY = "cn_user_id";

  function readUserId() {
    try { return localStorage.getItem(USER_ID_KEY) || null; } catch (e) { return null; }
  }

  // Stamp the stored user_id onto the request body so business_profiles.user_id
  // is never null after a logged-in user creates or updates their profile.
  function withUserId(form) {
    const userId = readUserId();
    if (!userId) return form;
    return { ...form, user_id: userId };
  }

  // ── Local state helpers ───────────────────────────────────────────────
  function isLoggedIn() {
    try { return !!localStorage.getItem(AUTH_KEY); } catch (e) { return false; }
  }

  function readPendingProfile() {
    try {
      const raw = localStorage.getItem(PENDING_PROFILE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function writePendingProfile(form) {
    try { localStorage.setItem(PENDING_PROFILE_KEY, JSON.stringify(form)); } catch (e) {}
  }

  function clearPendingProfile() {
    try { localStorage.removeItem(PENDING_PROFILE_KEY); } catch (e) {}
  }

  function readPendingPlan() {
    try {
      const raw = localStorage.getItem(PENDING_PLAN_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function writePendingPlan(plan) {
    try {
      if (plan) localStorage.setItem(PENDING_PLAN_KEY, JSON.stringify(plan));
      else localStorage.removeItem(PENDING_PLAN_KEY);
    } catch (e) {}
  }

  function clearPendingPlan() {
    try { localStorage.removeItem(PENDING_PLAN_KEY); } catch (e) {}
  }

  function readProfileId() {
    try { return localStorage.getItem(PROFILE_ID_KEY) || null; } catch (e) { return null; }
  }

  function writeProfileId(id) {
    try { if (id) localStorage.setItem(PROFILE_ID_KEY, id); } catch (e) {}
  }

  function clearProfileId() {
    try { localStorage.removeItem(PROFILE_ID_KEY); } catch (e) {}
  }

  // ── HTTP ──────────────────────────────────────────────────────────────
  async function request(path, { method = "GET", body } = {}) {
    const resp = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data.ok === false) {
      throw new Error(data.error || `Request failed (${resp.status})`);
    }
    return data;
  }

  // ── Profile API ───────────────────────────────────────────────────────
  async function createProfile(form) {
    const data = await request("/profile", { method: "POST", body: withUserId(form) });
    if (data.business_profile_id) writeProfileId(data.business_profile_id);
    return data;
  }

  async function updateProfile(profileId, form) {
    const data = await request(`/profile/${profileId}`, {
      method: "PUT",
      body: withUserId(form),
    });
    if (data.business_profile_id) writeProfileId(data.business_profile_id);
    return data;
  }

  // Preview expansion — no DB write. Returns { search_plan }.
  async function expandProfile(form) {
    return request("/profile/expand", { method: "POST", body: form });
  }

  // Onboarding v2 — infer a complete profile from either a website URL or
  // a one-line description. Returns { form, search_plan }. No DB write.
  async function inferProfile({ mode, website, description }) {
    return request("/profile/infer", {
      method: "POST",
      body: { mode, website, description },
    });
  }

  // Re-run expansion for an existing profile, persisting the new plan.
  async function expandSavedProfile(profileId) {
    return request(`/profile/${profileId}/expand`, { method: "POST" });
  }

  // Persist a manually-edited search plan.
  async function saveSearchPlan(profileId, plan) {
    return request(`/profile/${profileId}/search_plan`, {
      method: "PUT",
      body: { search_plan: plan },
    });
  }

  async function saveRadar(profileId, { subreddits, suggestedSubreddits }) {
    return request(`/profile/${profileId}/radar`, {
      method: "PUT",
      body: {
        subreddits,
        suggested_subreddits: suggestedSubreddits,
      },
    });
  }

  async function getProfile(profileId) {
    return request(`/profile/${profileId}`);
  }

  async function listProfiles(userId) {
    const id = userId || readUserId();
    if (!id) return { ok: true, profiles: [] };
    const qs = new URLSearchParams({ user_id: id });
    return request(`/profiles?${qs.toString()}`);
  }

  async function deleteProfile(profileId) {
    const data = await request(`/profile/${profileId}`, {
      method: "DELETE",
    });
    return data;
  }

  // Wipe opportunities, feedback, scan history, and candidate matches for
  // one profile. The profile itself (and its embeddings/search plan/radar)
  // stays — so the next scan starts from a clean derived-state baseline.
  async function clearProfileResults(profileId) {
    return request(`/profile/${profileId}/results`, { method: "DELETE" });
  }

  // Returns { export: { exported_at, profile, opportunities, opportunity_feedback,
  // opportunity_scans } } — the caller is responsible for turning it into a download.
  async function exportProfileResults(profileId) {
    return request(`/profile/${profileId}/export`);
  }

  async function deleteAccount(identity) {
    const body = {};
    if (identity?.userId) body.user_id = identity.userId;
    if (identity?.email) body.email = identity.email;
    const data = await request("/account", {
      method: "DELETE",
      body,
    });
    clearProfileId();
    clearPendingProfile();
    clearPendingPlan();
    return data;
  }

  // Save = create-if-no-id, update-if-id. Convenience wrapper.
  async function saveProfile(form) {
    const profileId = readProfileId();
    if (profileId) return updateProfile(profileId, form);
    return createProfile(form);
  }

  async function flushPendingProfile() {
    const pending = readPendingProfile();
    if (!pending) return null;
    const pendingPlan = readPendingPlan();
    const payload = pendingPlan ? { ...pending, search_plan: pendingPlan } : pending;
    const data = await saveProfile(payload);
    clearPendingProfile();
    clearPendingPlan();
    return data;
  }

  // ── Scans API ─────────────────────────────────────────────────────────
  // Cache-aware ingest: pulls last `window_days` of posts/comments for every
  // subreddit on the profile's Subreddit Radar, doing a delta fetch where
  // the cache is fresh and pruning anything older than the window. Returns
  // a summary the caller can drop straight into a toast.
  async function ingestProfile(profileId, { windowDays, subreddits } = {}) {
    const body = {};
    if (typeof windowDays === "number") body.window_days = windowDays;
    if (Array.isArray(subreddits) && subreddits.length) {
      body.subreddits = subreddits;
    }
    return request(`/ingest/profile/${profileId}`, { method: "POST", body });
  }

  async function runScan(profileId, opts = {}) {
    return request(`/scan/${profileId}`, { method: "POST", body: withUserId(opts) });
  }

  async function listScans(profileId) {
    const path = profileId ? `/scans/${profileId}` : "/scans";
    return request(path);
  }

  // Persistent opportunity inbox for a profile — returns every active
  // LLM-judged opportunity ever surfaced, not just rows from the latest
  // scan. The companion `scan` block on the response carries the latest
  // scan summary so the dashboard can render "Last scan: X checked,
  // Y new — Z still active".
  async function latestOpportunities(
    profileId,
    { includeDismissed = false, limit = 200 } = {},
  ) {
    const qs = new URLSearchParams({
      include_dismissed: includeDismissed ? "true" : "false",
      limit: String(limit),
    });
    return request(`/opportunities/${profileId}?${qs.toString()}`);
  }

  // Persist a feedback signal on one opportunity card. The backend writes
  // an audit row to opportunity_feedback AND rolls the verdict onto
  // opportunities.status so the inbox filters apply immediately. Pass
  // feedbackType=null to clear the user's prior label.
  async function submitFeedback(profileId, contentItemId, feedbackType, note) {
    if (!profileId || !contentItemId) return null;
    return request(`/feedback/${profileId}/${contentItemId}`, {
      method: "POST",
      body: { feedback_type: feedbackType ?? null, note: note ?? null },
    });
  }

  async function deleteOpportunity(profileId, opportunityId) {
    if (!profileId || !opportunityId) return null;
    return request(`/opportunity/${profileId}/${opportunityId}`, {
      method: "DELETE",
    });
  }

  // Result rows for one specific scan — works for any past scan, so the
  // Scans detail page can show older scans' matches, not just the latest.
  async function opportunitiesForScan(scanId, { onlySurfaced = true, limit = 800 } = {}) {
    const qs = new URLSearchParams({
      only_surfaced: onlySurfaced ? "true" : "false",
      limit: String(limit),
    });
    return request(`/scan-results/${scanId}?${qs.toString()}`);
  }

  // The candidates a scan checked but filtered out (didn't surface). Returns
  // raw stored rows (no LLM-judged copy) so the count matches the funnel's
  // "Filtered out" stat. Powers the tappable "Filtered out" list on the scan
  // detail pages (web + mobile web).
  async function filteredResultsForScan(scanId, { limit = 800 } = {}) {
    const qs = new URLSearchParams({
      only_surfaced: "false",
      include_unjudged: "true",
      limit: String(limit),
    });
    return request(`/scan-results/${scanId}?${qs.toString()}`);
  }

  async function deleteScan(scanId) {
    if (!scanId) throw new Error("Scan id is required.");
    return request(`/scan-results/${scanId}`, { method: "DELETE" });
  }

  async function stopScan(scanId) {
    if (!scanId) throw new Error("Scan id is required.");
    return request(`/scan-results/${scanId}/stop`, { method: "POST" });
  }

  // ── Mapping: scan_result row → opp card shape ─────────────────────────
  // The backend now hands the frontend ONLY LLM-judged rows. Every row has
  // a `judged` block (opportunity_score 0..100, polished copy, intent_type,
  // confidence_label, optional caution). Reddit context (title, body,
  // permalink, score, comments, created_at) still comes from the raw
  // scan-result columns. No heuristic fallback — if `judged` is missing,
  // the row is dropped.

  // Parse the reddit POST id out of any Reddit permalink — works for both
  // post permalinks (.../comments/{POST_ID}/{slug}/) and comment permalinks
  // (.../comments/{POST_ID}/_/{COMMENT_ID}/). Returns null if the URL doesn't
  // match. Used to cluster comment opportunities that live in the same
  // thread without needing a backend column.
  function redditPostIdFromPermalink(permalink) {
    if (!permalink || typeof permalink !== "string") return null;
    const m = permalink.match(/\/comments\/([a-z0-9]+)(?:\/|$)/i);
    return m ? m[1] : null;
  }
  function redditCommentIdFromPermalink(permalink) {
    if (!permalink || typeof permalink !== "string") return null;
    // Comment permalinks are .../comments/{POST_ID}/{slug-or-underscore}/{COMMENT_ID}/
    const m = permalink.match(/\/comments\/[a-z0-9]+\/[^/]+\/([a-z0-9]+)/i);
    return m ? m[1] : null;
  }

  function tierForConfidenceLabel(label) {
    const v = (label || "").toLowerCase();
    if (v === "high") return "high";
    if (v === "medium") return "medium";
    if (v === "low") return "low";
    return null;
  }
  function tierForScore(score) {
    if (score >= 82) return "high";
    if (score >= 68) return "medium";
    return "low";
  }

  function relativeTime(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "—";
    const diffH = Math.max(0, (Date.now() - t) / 3_600_000);
    if (diffH < 1) return `${Math.max(1, Math.round(diffH * 60))}m ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    const d = Math.round(diffH / 24);
    return `${d}d ago`;
  }
  // Shared on window for screens that want to format a timestamp the same
  // way as opp cards.
  function relativeAgo(input) {
    if (!input) return "—";
    if (typeof input === "number") {
      const diffMs = Math.max(0, Date.now() - input);
      if (diffMs < 5_000) return "just now";
      if (diffMs < 60_000) return `${Math.round(diffMs / 1000)}s ago`;
      if (diffMs < 3_600_000) return `${Math.round(diffMs / 60_000)}m ago`;
      const h = Math.round(diffMs / 3_600_000);
      if (h < 24) return `${h}h ago`;
      const d = Math.round(h / 24);
      return `${d}d ago`;
    }
    return relativeTime(input);
  }
  window.relativeAgo = relativeAgo;

  // Auto-ticking timestamp pill — re-renders every 15s so "Synced 30s ago"
  // doesn't lie. The component is intentionally tiny; importing it via
  // window.* lets any screen drop it in without a build step.
  function RelativeUpdated({ at }) {
    const [, force] = React.useState(0);
    React.useEffect(() => {
      const id = setInterval(() => force((n) => n + 1), 15_000);
      return () => clearInterval(id);
    }, []);
    return React.createElement(React.Fragment, null, relativeAgo(at));
  }
  window.RelativeUpdated = RelativeUpdated;

  // Map a backend opportunity row to the shape every UI screen expects.
  // The row can come from two endpoints:
  //   /opportunities/:profile_id   → persistent inbox (opportunity_id stable)
  //   /scan-results/:scan_id       → per-scan history (scan_result_id stable)
  // Both shapes have a `judged` block carrying the polished LLM payload;
  // rows without it are not LLM-classified and we drop them.
  //
  // We prefer the durable `opportunity_id` for the card's React key when
  // present, so feedback/seen state survives scan_result row regeneration.
  function statusToFeedback(status) {
    if (!status) return null;
    if (status === "saved") return "relevant";
    if (status === "not_relevant") return "not_relevant";
    if (status === "dismissed") return "dismissed";
    return null;
  }

  function mapScanResultToOpp(row) {
    const judged = row?.judged;
    if (!judged) return null;

    const subreddit = row.subreddit_name
      ? (row.subreddit_name.startsWith("r/") ? row.subreddit_name : `r/${row.subreddit_name}`)
      : "r/unknown";
    const body = row.body || "";
    const excerpt = body.length > 360 ? `${body.slice(0, 360).trim()}…` : body;

    const score = typeof judged.opportunity_score === "number"
      ? judged.opportunity_score
      : 0;
    const tier = tierForConfidenceLabel(judged.confidence_label) || tierForScore(score);
    const status = judged.status || row.status || null;

    const kind = row.source_type === "reddit_comment" ? "comment" : "post";
    // Stable thread key for clustering comment opportunities in the feed:
    // both posts and comments expose the same post id. Parsed from permalink
    // (the source row doesn't carry it as a column).
    const redditPostId = redditPostIdFromPermalink(row.permalink);
    const redditCommentId = kind === "comment"
      ? redditCommentIdFromPermalink(row.permalink)
      : null;

    return {
      // Durable opp id when the row comes from the inbox endpoint; fall
      // back to the per-scan result id for the scans history view.
      id: row.opportunity_id || row.id,
      opportunityId: row.opportunity_id || null,
      contentItemId: row.content_item_id,
      subreddit,
      kind,
      redditPostId,
      redditCommentId,
      // Headline is the LLM-written, human-friendly summary; the raw Reddit
      // title is preserved for "Original post" / "Comment in:" prefixes.
      title: judged.headline || row.title || "(untitled)",
      redditTitle: row.title || null,
      excerpt,
      score,
      tier,
      intent: judged.intent_type || "Discussion",
      useCase: judged.matched_use_case || "—",
      persona: judged.customer_match || "—",
      pain: judged.matched_pain_point || "—",
      why: judged.why_it_matched || "—",
      angle: judged.suggested_angle || "—",
      caution: judged.caution || null,
      judged: true,
      confidence: judged.confidence_label || null,
      posted: relativeTime(row.reddit_created_utc),
      postedAt: row.reddit_created_utc || null,
      comments: row.num_comments || 0,
      upvotes: row.reddit_score || 0,
      permalink: row.permalink || null,
      authorName: row.author_name || null,
      status,
      firstSeenScanId: row.first_seen_scan_id || null,
      latestScanId: row.latest_scan_id || null,
      // Backend-derived "first surfaced by the most recent scan" — drives
      // the "New" dot and the dashboard's "X new this scan" count. The
      // client overrides it to false once the user has interacted with the
      // card in this session (see seenOppIdsRef in app.jsx).
      isNew: row.is_new_this_scan === true,
      feedback: statusToFeedback(status),
      threadContext: body,
      _raw: row,
    };
  }

  // Convenience: takes the full results array and drops un-judged rows.
  function mapResults(rows) {
    if (!Array.isArray(rows)) return [];
    return rows.map(mapScanResultToOpp).filter(Boolean);
  }

  // ── Filtered-out candidates (scan detail) ─────────────────────────────
  // Raw rows the scan checked but didn't surface. No judged copy — just the
  // Reddit context + heuristic score, mapped into a light card shape.
  function filteredTier(score) {
    if (score >= 50) return "high";
    if (score >= 30) return "medium";
    return "low";
  }

  function mapFilteredRow(row) {
    if (!row || row.surfaced === true) return null;
    const subreddit = row.subreddit_name
      ? (row.subreddit_name.startsWith("r/") ? row.subreddit_name : `r/${row.subreddit_name}`)
      : "r/unknown";
    const body = row.body || "";
    const excerpt = body.length > 220 ? `${body.slice(0, 220).trim()}…` : body;
    const score = typeof row.final_score === "number" ? Math.round(row.final_score * 100) : 0;
    const kind = row.source_type === "reddit_comment" ? "comment" : "post";
    return {
      id: row.id,
      subreddit,
      kind,
      title: row.title || excerpt || "(untitled)",
      excerpt,
      body,
      score,
      tier: filteredTier(score),
      posted: relativeTime(row.reddit_created_utc),
      postedAt: row.reddit_created_utc || null,
      comments: row.num_comments || 0,
      upvotes: row.reddit_score || 0,
      permalink: row.permalink || null,
      authorName: row.author_name || null,
    };
  }

  function mapFilteredResults(rows) {
    if (!Array.isArray(rows)) return [];
    return rows.map(mapFilteredRow).filter(Boolean);
  }

  // Adapt a filtered candidate into the opp shape GenerateResponseDialog
  // expects. Judged-only fields are blanked (the raw reply route ignores
  // them); the body, permalink and author carry over so the draft has real
  // context and posting can resolve a parent thing id.
  function filteredToOpp(item) {
    if (!item) return null;
    const redditPostId = redditPostIdFromPermalink(item.permalink);
    const redditCommentId =
      item.kind === "comment" ? redditCommentIdFromPermalink(item.permalink) : null;
    const text = item.body || item.excerpt;
    return {
      id: item.id,
      opportunityId: null,
      contentItemId: null,
      subreddit: item.subreddit,
      kind: item.kind,
      redditPostId,
      redditCommentId,
      title: item.title,
      redditTitle: item.title,
      excerpt: text,
      score: item.score,
      tier: item.tier,
      intent: "Discussion",
      useCase: "—",
      persona: "—",
      pain: "—",
      why: "—",
      angle: "—",
      caution: null,
      judged: false,
      confidence: null,
      posted: item.posted,
      postedAt: item.postedAt,
      comments: item.comments,
      upvotes: item.upvotes,
      permalink: item.permalink,
      authorName: item.authorName,
      status: null,
      isNew: false,
      threadContext: text,
      feedback: null,
    };
  }

  // ── Notification preferences API ──────────────────────────────────────
  // Backs the Notifications screen. The screen autosaves on every change,
  // so updatePreferences is debounced from the React side, not here.
  async function getPreferences(userId) {
    const id = userId || readUserId();
    if (!id) return { ok: true, preferences: null };
    return request(`/preferences/${encodeURIComponent(id)}`);
  }

  async function updatePreferences(userId, patch) {
    const id = userId || readUserId();
    if (!id) return null;
    return request(`/preferences/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: patch || {},
    });
  }

  // ── Digest API ────────────────────────────────────────────────────────
  // Send a one-off "Today's Brief" email built from the profile's latest
  // active opportunities. Eventually the cron job will use the same
  // backend renderer; for now the Notifications "Test Now" button is the
  // only caller.
  async function sendTestDigest(profileId, { email, name, theme } = {}) {
    if (!profileId) throw new Error("Active business profile is required.");
    if (!email) throw new Error("Email address is required.");
    return request(`/digest/test/${profileId}`, {
      method: "POST",
      body: { email, name, theme },
    });
  }

  // ── Contact API ───────────────────────────────────────────────────────
  // Lightweight public contact form used from the shared legal footer.
  async function submitContactMessage({ email, message, source } = {}) {
    const trimmedEmail = String(email || "").trim();
    const trimmedMessage = String(message || "").trim();
    if (!trimmedEmail) throw new Error("Email is required.");
    if (!trimmedMessage) throw new Error("Message is required.");
    return request("/contact", {
      method: "POST",
      body: {
        email: trimmedEmail,
        message: trimmedMessage,
        source: source || "site",
        user_id: readUserId() || null,
      },
    });
  }

  // ── Billing (Stripe) ──────────────────────────────────────────────────
  // The Pricing screen + Settings → Subscription both call these. We never
  // touch Stripe directly from the browser — the backend mints Checkout /
  // Portal URLs (with the user's email + customer id stamped server-side)
  // and we just window.location.assign() the returned URL.
  async function listPlans() {
    return request("/billing/plans");
  }

  async function getSubscription(userId) {
    const id = userId || readUserId();
    if (!id) return { ok: true, subscription: null, usage: null };
    return request(`/billing/subscription/${encodeURIComponent(id)}`);
  }

  async function startCheckout({ planId = "solo", email } = {}) {
    const userId = readUserId();
    if (!userId) throw new Error("Sign in to subscribe.");
    const resp = await request("/billing/checkout", {
      method: "POST",
      body: { user_id: userId, email: email || null, plan_id: planId },
    });
    if (resp?.url) window.location.assign(resp.url);
    return resp;
  }

  async function openBillingPortal() {
    const userId = readUserId();
    if (!userId) throw new Error("Sign in to manage billing.");
    const resp = await request("/billing/portal", {
      method: "POST",
      body: { user_id: userId },
    });
    if (resp?.url) {
      const win = window.open(resp.url, "_blank", "noopener,noreferrer");
    }
    return resp;
  }

  // ── Reddit account connection + reply generation/posting ─────────────
  // All Reddit API calls stay server-side; the frontend only ever sees the
  // connection STATUS (username), never any access/refresh tokens.
  async function getRedditConnection(userId) {
    const id = userId || readUserId();
    if (!id) return { ok: true, connection: { connected: false } };
    return request(`/reddit/connection/${encodeURIComponent(id)}`);
  }

  // Returns Reddit's authorize URL; the caller redirects the browser to it.
  // redirectTo is an optional frontend path the callback bounces back to.
  async function startRedditOAuth(userId, redirectTo) {
    const id = userId || readUserId();
    if (!id) throw new Error("Sign in to connect Reddit.");
    const qs = new URLSearchParams({ user_id: id });
    if (redirectTo) qs.set("redirect_to", redirectTo);
    const resp = await request(`/reddit/oauth/start?${qs.toString()}`);
    return resp;
  }

  async function disconnectReddit(userId) {
    const id = userId || readUserId();
    if (!id) throw new Error("Sign in to manage Reddit.");
    return request("/reddit/disconnect", {
      method: "POST",
      body: { user_id: id },
    });
  }

  // Whether a reply was already posted to an opportunity (+ deep link). Used
  // by the Generate dialog to show "view your reply" and warn before reposting.
  async function redditPostedStatus(opportunityId) {
    if (!opportunityId) return { ok: true, posted: false };
    return request(`/reddit/posted/${encodeURIComponent(opportunityId)}`);
  }

  async function generateRedditReply({ profileId, opportunity, tone, toneId } = {}) {
    return request("/reddit/generate-reply", {
      method: "POST",
      body: {
        profile_id: profileId || readProfileId() || null,
        opportunity: opportunity || {},
        tone: tone || "helpful",
        tone_id: toneId || null,
        user_id: readUserId() || null,
      },
    });
  }

  // ── Custom reply tones ──────────────────────────────────────────────
  // Returns { tones: [custom...], default: "<builtin-key|custom-id>" }. The
  // built-in presets are static on the client and merged in by the UI.
  async function listReplyTones(userId) {
    const id = userId || readUserId();
    if (!id) return { ok: true, tones: [], default: "helpful" };
    return request(`/reddit/reply-tones/${encodeURIComponent(id)}`);
  }

  async function createReplyTone({ name, instructions, productMode, makeDefault } = {}) {
    const id = readUserId();
    if (!id) throw new Error("Sign in to save a tone.");
    return request("/reddit/reply-tones", {
      method: "POST",
      body: {
        user_id: id, name, instructions,
        product_mode: productMode || "soft",
        make_default: !!makeDefault,
      },
    });
  }

  async function updateReplyTone(toneId, { name, instructions, productMode } = {}) {
    const id = readUserId();
    if (!id) throw new Error("Sign in to edit a tone.");
    if (!toneId) throw new Error("toneId is required.");
    return request(`/reddit/reply-tones/${encodeURIComponent(toneId)}`, {
      method: "PUT",
      body: { user_id: id, name, instructions, product_mode: productMode },
    });
  }

  async function deleteReplyTone(toneId) {
    const id = readUserId();
    if (!id) throw new Error("Sign in to delete a tone.");
    if (!toneId) throw new Error("toneId is required.");
    return request(`/reddit/reply-tones/${encodeURIComponent(toneId)}`, {
      method: "DELETE",
      body: { user_id: id },
    });
  }

  async function setDefaultReplyTone(tone) {
    const id = readUserId();
    if (!id) throw new Error("Sign in to set a default tone.");
    return request("/reddit/reply-tones/set-default", {
      method: "POST",
      body: { user_id: id, tone },
    });
  }

  // Raw fetch (not the throwing `request` helper) so the caller can read the
  // `code` field — notably "duplicate_post", which drives the confirm-and-
  // retry-with-force flow without treating it as a hard failure.
  async function postRedditReply({
    userId,
    parentThingId,
    text,
    opportunityId,
    subreddit,
    force = false,
  } = {}) {
    const id = userId || readUserId();
    if (!id) throw new Error("Sign in to post.");
    const resp = await fetch(`${BASE_URL}/reddit/post-reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: id,
        parent_thing_id: parentThingId,
        text,
        opportunity_id: opportunityId || null,
        subreddit: subreddit || null,
        force: !!force,
      }),
    });
    const data = await resp.json().catch(() => ({}));
    return { httpStatus: resp.status, ...data };
  }

  // ── Reddit private messages (DM the author) ─────────────────────────
  // Generate a private-message DRAFT to the author of an opportunity. Shares
  // the tone presets with reply drafting; the prompt frames a one-to-one DM
  // rather than a public reply. Returns the draft body only — nothing is sent.
  async function generateRedditPM({ profileId, opportunity, author, tone, toneId } = {}) {
    return request("/reddit/generate-pm", {
      method: "POST",
      body: {
        profile_id: profileId || readProfileId() || null,
        opportunity: opportunity || {},
        author: author || null,
        tone: tone || "helpful",
        tone_id: toneId || null,
        user_id: readUserId() || null,
      },
    });
  }

  // Send an (edited) private message from the user's connected Reddit account.
  // Raw fetch (not the throwing `request`) so the caller can read the `code`
  // field for clearer errors (e.g. "reddit_not_connected").
  async function sendRedditPM({ userId, recipient, subject, body } = {}) {
    const id = userId || readUserId();
    if (!id) throw new Error("Sign in to send.");
    const resp = await fetch(`${BASE_URL}/reddit/send-pm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: id,
        recipient,
        subject: subject || "",
        body,
      }),
    });
    const data = await resp.json().catch(() => ({}));
    return { httpStatus: resp.status, ...data };
  }

  // Conversations screen feed: { stats, conversations }. Pure read — no Reddit
  // calls happen server-side here.
  async function getRedditConversations(userId) {
    const id = userId || readUserId();
    if (!id) return { ok: true, stats: { replies: 0, total: 0, reply_rate: 0, awaiting: 0 }, conversations: [] };
    return request(`/reddit/conversations/${encodeURIComponent(id)}`);
  }

  // User-triggered refresh: re-polls a bounded, newest-first batch from Reddit.
  // Returns the feed plus { refreshed, remaining_stale, connected }.
  async function refreshRedditConversations(userId) {
    const id = userId || readUserId();
    if (!id) throw new Error("Sign in to refresh.");
    return request(`/reddit/conversations/${encodeURIComponent(id)}/refresh`, {
      method: "POST",
    });
  }

  // Delete one conversation. Always removes the local record; pass
  // { onReddit: true } to also delete the underlying comment on Reddit.
  async function deleteRedditConversation(conversationId, { onReddit = false } = {}, userId) {
    const id = userId || readUserId();
    if (!id) throw new Error("Sign in to delete.");
    if (!conversationId) throw new Error("conversationId is required.");
    return request(
      `/reddit/conversations/${encodeURIComponent(id)}/${encodeURIComponent(conversationId)}`,
      { method: "DELETE", body: { on_reddit: !!onReddit } },
    );
  }

  // ── usePlan hook — shared entitlements ────────────────────────────────
  // Single shared cache: every component that calls usePlan() gets the
  // same fetched row, and an in-flight request is deduped so mounting
  // five components at once issues exactly one /billing/subscription GET.
  // The cache TTLs after PLAN_TTL_MS so a stale plan can't outlive a
  // billing change, and callers can explicitly invalidate it after a
  // billing mutation or foreground refresh.
  const FREE_LIMITS = {
    max_projects: 1,
    max_subreddits: 3,
    max_visible_opportunities: 15,
    allowed_scan_windows: ["24h", "since-last"],
    monthly_scan_quota: 6,
    daily_digest_enabled: false,
    feedback_learning_enabled: false,
    automatic_daily_scan: false,
  };
  const PLAN_TTL_MS = 30_000;

  let _planCache = null;        // last fetched { plan, limits, usage, subscription, fetchedAt }
  let _planInFlight = null;     // shared in-flight Promise
  const _planSubscribers = new Set();
  const PlanContext = React.createContext(null);

  function _emit() {
    for (const fn of _planSubscribers) {
      try { fn(); } catch (e) {}
    }
  }

  function _stateFromCache() {
    if (!_planCache) {
      return {
        loading: true,
        plan: "free",
        limits: FREE_LIMITS,
        usage: null,
        subscription: null,
        fetchedAt: null,
      };
    }
    return { loading: false, fetchedAt: _planCache.fetchedAt || null, ...mapBillingResponse(_planCache) };
  }

  function mapBillingResponse(resp) {
    const sub = resp?.subscription || null;
    const usage = resp?.usage || null;
    const planId = sub?.plan || "free";
    const limits = sub?.plan_definition?.limits
      || (planId === "free" ? FREE_LIMITS : null)
      || FREE_LIMITS;
    return { plan: planId, limits, usage, subscription: sub };
  }

  async function _fetchPlan(force) {
    const now = Date.now();
    const userId = readUserId() || null;
    if (_planCache && _planCache.userId !== userId) {
      _planCache = null;
    }
    if (!force && _planCache && now - _planCache.fetchedAt < PLAN_TTL_MS) {
      return _planCache;
    }
    if (_planInFlight) return _planInFlight;
    _planInFlight = (async () => {
      try {
        const resp = await getSubscription(userId);
        _planCache = { ...resp, userId, fetchedAt: Date.now() };
        return _planCache;
      } finally {
        _planInFlight = null;
        _emit();
      }
    })();
    return _planInFlight;
  }

  function bumpPlanRefresh() {
    _planCache = null;
    _emit();
    _fetchPlan(true);
  }

  function usePlanState(enabled = true) {
    const [, force] = React.useState(0);
    React.useEffect(() => {
      if (!enabled) return undefined;
      const sub = () => force((n) => n + 1);
      _planSubscribers.add(sub);
      _fetchPlan(false);
      return () => { _planSubscribers.delete(sub); };
    }, [enabled]);
    return {
      ..._stateFromCache(),
      refresh: () => _fetchPlan(true),
      bumpPlanRefresh,
    };
  }
  function PlanProvider({ children }) {
    const value = usePlanState();
    return (
      <PlanContext.Provider value={value}>
        {children}
      </PlanContext.Provider>
    );
  }
  function usePlan() {
    const ctx = React.useContext(PlanContext);
    const fallback = usePlanState(!ctx);
    return ctx || fallback;
  }
  window.PlanProvider = PlanProvider;
  window.usePlan = usePlan;
  window.bumpPlanRefresh = bumpPlanRefresh;

  // ── Subreddit metadata (DB-first, PRAW fallback) ─────────────────────
  async function subredditInfo(name) {
    const qs = new URLSearchParams({ name: String(name || "").trim() });
    return request(`/subreddit/info?${qs.toString()}`);
  }

  // ── Health ────────────────────────────────────────────────────────────
  async function health() {
    return request("/health");
  }

  // ── Expose ────────────────────────────────────────────────────────────
  window.api = {
    BASE_URL,
    request,
    health,
    profile: {
      create: createProfile,
      update: updateProfile,
      get: getProfile,
      list: listProfiles,
      delete: deleteProfile,
      clearResults: clearProfileResults,
      exportResults: exportProfileResults,
      save: saveProfile,
      flushPending: flushPendingProfile,
      expand: expandProfile,
      expandSaved: expandSavedProfile,
      saveSearchPlan: saveSearchPlan,
      saveRadar: saveRadar,
      infer: inferProfile,
      deleteAccount: deleteAccount,
    },
    scan: {
      run: runScan,
      list: listScans,
      ingest: ingestProfile,
      delete: deleteScan,
      stop: stopScan,
    },
    subreddit: {
      info: subredditInfo,
    },
    opportunities: {
      latest: latestOpportunities,
      forScan: opportunitiesForScan,
      filteredForScan: filteredResultsForScan,
      mapResult: mapScanResultToOpp,
      mapResults: mapResults,
      mapFiltered: mapFilteredResults,
      filteredToOpp: filteredToOpp,
      feedback: submitFeedback,
      delete: deleteOpportunity,
    },
    digest: {
      testNow: sendTestDigest,
    },
    contact: {
      submit: submitContactMessage,
    },
    preferences: {
      get: getPreferences,
      update: updatePreferences,
    },
    billing: {
      plans: listPlans,
      subscription: getSubscription,
      checkout: startCheckout,
      portal: openBillingPortal,
    },
    reddit: {
      connection: getRedditConnection,
      startOAuth: startRedditOAuth,
      disconnect: disconnectReddit,
      generateReply: generateRedditReply,
      postReply: postRedditReply,
      generatePM: generateRedditPM,
      sendPM: sendRedditPM,
      postedStatus: redditPostedStatus,
      conversations: getRedditConversations,
      refreshConversations: refreshRedditConversations,
      deleteConversation: deleteRedditConversation,
      replyTones: {
        list: listReplyTones,
        create: createReplyTone,
        update: updateReplyTone,
        remove: deleteReplyTone,
        setDefault: setDefaultReplyTone,
      },
    },
    auth: {
      isLoggedIn,
      readUserId,
    },
    storage: {
      readPendingProfile,
      writePendingProfile,
      clearPendingProfile,
      readPendingPlan,
      writePendingPlan,
      clearPendingPlan,
      readProfileId,
      writeProfileId,
      clearProfileId,
    },
  };
})();
